import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Receipt,
  Send
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import expenseClaimService from '../../services/expenseClaimService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ExpenseClaim = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalClaims: 0,
    draftClaims: 0,
    submittedClaims: 0,
    approvedClaims: 0,
    rejectedClaims: 0,
    totalAmount: 0,
    pendingReimbursement: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [submitItem, setSubmitItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [claimsResponse, statsResponse] = await Promise.all([
        expenseClaimService.getAllClaims(),
        expenseClaimService.getClaimStats()
      ])
      
      if (claimsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (claimsResponse.data || []).map(claim => ({
          ...claim,
          id: claim._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (claimsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No expense claim records found. Create your first claim!')
        }
      } else {
        toast.error(claimsResponse.message || 'Failed to load claims')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading expense claim records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view expense claims')
      } else {
        toast.error('Failed to load expense claim records')
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Submitted': 'pending',
      'Under Review': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Partially Approved': 'warning',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'claimNumber',
      header: 'Claim Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.employeeCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.employeeCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'expenseDate',
      header: 'Expense Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'grandTotal',
      header: 'Amount',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue()
        const badgeClass = getStatusBadgeClass(status)
        return (
          <div className="active-cell" style={{ textAlign: 'center' }}>
            <span className={`status-badge ${badgeClass}`}>
              {status || '-'}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'reimbursementStatus',
      header: 'Reimbursement',
      cell: ({ getValue }) => {
        const status = getValue()
        const badgeClass = status === 'Paid' ? 'active' : status === 'Pending' ? 'pending' : 'inactive'
        return (
          <div className="active-cell" style={{ textAlign: 'center' }}>
            <span className={`status-badge ${badgeClass}`}>
              {status || 'Pending'}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'approverName',
      header: 'Approver',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          <span>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/expenses/expense-claim/form')
  }

  const handleEdit = (claimData) => {
    if (claimData.status !== 'Draft') {
      toast.warning('Only draft claims can be edited')
      return
    }
    navigate('/expenses/expense-claim/edit', { state: { claimData } })
  }

  const handleView = (claimData) => {
    navigate('/expenses/expense-claim/view', { state: { claimData } })
  }

  const handleDelete = (claimData) => {
    if (claimData.status !== 'Draft') {
      toast.warning('Only draft claims can be deleted')
      return
    }
    setDeleteItem(claimData)
    setShowDeleteModal(true)
  }

  const handleSubmit = (claimData) => {
    if (claimData.status !== 'Draft') {
      toast.warning('Only draft claims can be submitted for approval')
      return
    }
    setSubmitItem(claimData)
    setShowSubmitModal(true)
  }

  const confirmSubmit = async () => {
    if (!submitItem) return

    try {
      toast.info('Submitting expense claim for approval...')
      const response = await expenseClaimService.submitClaim(submitItem._id)
      if (response.success) {
        toast.success('Expense claim submitted for approval successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to submit expense claim')
      }
    } catch (error) {
      console.error('Error submitting expense claim:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Cannot submit this claim')
      } else if (error.response?.status === 404) {
        toast.error('Expense claim not found')
      } else {
        toast.error('Failed to submit expense claim. Please try again.')
      }
    } finally {
      setShowSubmitModal(false)
      setSubmitItem(null)
    }
  }

  const cancelSubmit = () => {
    setShowSubmitModal(false)
    setSubmitItem(null)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting expense claim...')
      const response = await expenseClaimService.deleteClaim(deleteItem._id)
      if (response.success) {
        toast.success('Expense claim deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete expense claim')
      }
    } catch (error) {
      console.error('Error deleting expense claim:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Cannot delete this claim')
      } else if (error.response?.status === 404) {
        toast.error('Expense claim not found')
      } else {
        toast.error('Failed to delete expense claim. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Expenses',
      path: '/expenses',
      onClick: () => navigate('/expenses')
    },
    {
      label: 'Expense Claims',
      path: '/expenses/expense-claim',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Expense Claims</h1>
          <p>Employee expense submissions and receipts</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Claim
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalClaims}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalClaims}</div>
          <div className="metric-title">Total Claims</div>
          <div className="metric-subtitle">All expense claims</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.submittedClaims}</span>
            </div>
            <div className="metric-trend positive">
              <span>Submitted</span>
            </div>
          </div>
          <div className="metric-value">{stats.submittedClaims}</div>
          <div className="metric-title">Submitted</div>
          <div className="metric-subtitle">Awaiting approval</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.approvedClaims}</span>
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedClaims}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Approved claims</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{formatCurrency(stats.pendingReimbursement)}</span>
            </div>
            <div className="metric-trend negative">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.pendingReimbursement)}</div>
          <div className="metric-title">Pending Reimbursement</div>
          <div className="metric-subtitle">Amount pending</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          customActions={(row) => {
            const actions = [
              { type: 'view', label: 'View Details', onClick: () => handleView(row) },
            ]
            
            // Only show Edit and Delete for Draft claims
            if (row.status === 'Draft') {
              actions.push(
                { type: 'edit', label: 'Edit', onClick: () => handleEdit(row) },
                { type: 'submit', label: 'Submit for Approval', icon: <Send size={16} />, onClick: () => handleSubmit(row) },
                { type: 'delete', label: 'Delete', onClick: () => handleDelete(row) }
              )
            }
            
            return actions
          }}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Expense Claim"
        message={`Are you sure you want to delete claim "${deleteItem?.claimNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Submit Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSubmitModal}
        onClose={cancelSubmit}
        onConfirm={confirmSubmit}
        title="Submit Expense Claim for Approval"
        message={`Are you sure you want to submit claim "${submitItem?.claimNumber}" for approval? Once submitted, you will not be able to edit this claim.`}
        confirmText="Submit"
        cancelText="Cancel"
        type="info"
      />
    </div>
  )
}

export default ExpenseClaim

